import React from 'react'

import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react'
import { observer } from 'mobx-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import { Badge } from '~/client/src/shared/components/shadcn-ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/client/src/shared/components/shadcn-ui/select'
import { Toggle } from '~/client/src/shared/components/shadcn-ui/toggle'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { getDateRangeOptions } from '../../Constants/DateRanges'
import { DateRangeType } from '../../types/Widgets'
import DateRangeSelectorStore from './DateRangeSelector.store'

interface DateRangeSelectorProps {
  store: DateRangeSelectorStore
  onChange?: (dateRange: any) => void
}

@observer
export default class DateRangeSelector extends React.Component<DateRangeSelectorProps> {
  public render() {
    const { store } = this.props
    const options = getDateRangeOptions()

    return (
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:max-[768px]:gap-1.5 tw:max-[480px]:order-[-1] tw:max-[480px]:mb-1 tw:max-[480px]:w-full tw:max-[480px]:justify-between">
        <Toggle
          variant="neutral"
          pressed={store.periodOffset === 0}
          onPressedChange={this.handleResetToToday}
        >
          {Localization.translator.today}
        </Toggle>

        <div className="tw:flex tw:items-center tw:gap-0.5 tw:max-[480px]:flex-1 tw:max-[480px]:justify-center">
          <Badge variant="outline">
            {store.formattedDateDisplay}
            <Calendar data-icon="inline-end" />
          </Badge>

          <Button
            variant="ghost"
            size="icon"
            onClick={this.handleNavigatePrevious}
            title={Localization.translator.previous}
          >
            <ChevronLeft />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={this.handleNavigateNext}
            disabled={!store.canNavigateNext}
            title={Localization.translator.next}
          >
            <ChevronRight />
          </Button>
        </div>

        <Select
          value={store.selectedType}
          onValueChange={value =>
            this.handleRangeSelect(value as DateRangeType)
          }
          items={options}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  }

  private handleResetToToday = () => {
    const { store, onChange } = this.props
    store.resetToToday()

    if (onChange) {
      onChange(store.dateRange)
    }
  }

  private handleRangeSelect = (type: DateRangeType) => {
    const { store, onChange } = this.props
    store.setDateRangeType(type)

    if (onChange) {
      onChange(store.dateRange)
    }
  }

  private handleNavigatePrevious = () => {
    const { store, onChange } = this.props
    store.navigatePrevious()

    if (onChange) {
      onChange(store.dateRange)
    }
  }

  private handleNavigateNext = () => {
    const { store, onChange } = this.props
    store.navigateNext()

    if (onChange) {
      onChange(store.dateRange)
    }
  }
}
